import { multiLanguage } from "@db-models/pricing-db.model";

// Comparison-table data for the collapsible "Compare all features" panel.
// Rows are organized by category. Cells are one of:
//   - true / false  → renders ✓ / —
//   - string        → renders literal text (e.g., "5", "Add-on", "Limited")
//   - { addon: true } → renders "+" with brand-warning tag indicating add-on
// Bilingual content baked in via the multiLanguage shape; no separate i18n
// keys needed for row labels (translators edit this one file).

export type ComparisonCell = boolean | string | { addon: true };

export interface ComparisonRow {
  feature: multiLanguage;
  basic: ComparisonCell;
  starter: ComparisonCell;
  team: ComparisonCell;
  enterprise: ComparisonCell;
}

export interface ComparisonCategory {
  title: multiLanguage;
  rows: ComparisonRow[];
}

const __: (en: string, de: string) => multiLanguage = (en_US, de_CH) => ({ en_US, de_CH });

export const V2_COMPARISON: ComparisonCategory[] = [
  {
    title: __('Capacity & limits', 'Kapazität & Limits'),
    rows: [
      { feature: __('Resources / users', 'Ressourcen / Benutzer'),     basic: '1',  starter: '5',  team: '15', enterprise: '25+' },
      { feature: __('Branches / locations', 'Filialen / Standorte'),    basic: '1',  starter: '3',  team: '5',  enterprise: '10+' },
      { feature: __('Group appointments', 'Gruppentermine'),            basic: '1',  starter: '5',  team: '15', enterprise: '30+' },
      { feature: __('Conference appointments', 'Konferenztermine'),     basic: false, starter: false, team: '5', enterprise: '10+' },
      { feature: __('Monthly bookings', 'Buchungen pro Monat'),         basic: '∞',  starter: '∞',  team: '∞',  enterprise: '∞' },
      { feature: __('Webhook endpoints', 'Webhook-Endpunkte'),          basic: false, starter: '1', team: '5', enterprise: '∞' },
    ],
  },
  {
    title: __('Booking core', 'Buchungs-Kernfunktionen'),
    rows: [
      { feature: __('Online appointment booking', 'Online-Terminbuchung'),         basic: true,  starter: true, team: true, enterprise: true },
      { feature: __('Booking page customization', 'Buchungsseiten-Anpassung'),     basic: 'Limited', starter: true, team: true, enterprise: true },
      { feature: __('Embeddable booking widget', 'Einbettbares Buchungs-Widget'),  basic: true,  starter: true, team: true, enterprise: true },
      { feature: __('Custom forms', 'Individuelle Formulare'),                      basic: true,  starter: true, team: true, enterprise: true },
      { feature: __('Booking questions / custom fields', 'Buchungsfragen / Felder'), basic: 'Basic', starter: true, team: true, enterprise: true },
      { feature: __('Round-robin distribution', 'Round-Robin Verteilung'),         basic: false, starter: true, team: true, enterprise: true },
      { feature: __('Conference appointments', 'Konferenztermine'),                 basic: false, starter: false, team: true, enterprise: true },
      { feature: __('Multilingual booking', 'Mehrsprachige Buchung'),               basic: false, starter: false, team: true, enterprise: true },
      { feature: __('Qualification questions (routing)', 'Qualifizierungsfragen'),  basic: false, starter: false, team: true, enterprise: true },
      { feature: __('Booking cancellation & rescheduling', 'Stornierung & Umbuchung'), basic: false, starter: true, team: true, enterprise: true },
    ],
  },
  {
    title: __('Calendar sync', 'Kalendersynchronisation'),
    rows: [
      { feature: __('Google Calendar', 'Google Kalender'),                          basic: true, starter: true, team: true, enterprise: true },
      { feature: __('Apple iCloud / CalDAV', 'Apple iCloud / CalDAV'),              basic: true, starter: true, team: true, enterprise: true },
      { feature: __('Microsoft Office 365', 'Microsoft Office 365'),                basic: false, starter: true, team: true, enterprise: true },
      { feature: __('Exchange On-Premise / Impersonation', 'Exchange On-Premise / Impersonation'), basic: false, starter: false, team: false, enterprise: true },
      { feature: __('Office 365 Enterprise (multi-tenant)', 'Office 365 Enterprise (multi-tenant)'), basic: false, starter: false, team: false, enterprise: true },
      { feature: __('Dynamic availabilities', 'Dynamische Verfügbarkeiten'),        basic: false, starter: false, team: false, enterprise: true },
    ],
  },
  {
    title: __('Notifications', 'Benachrichtigungen'),
    rows: [
      { feature: __('Email confirmations & reminders', 'E-Mail-Bestätigungen & Erinnerungen'), basic: true, starter: true, team: true, enterprise: true },
      { feature: __('SMS notifications', 'SMS-Benachrichtigungen'),                 basic: false, starter: false, team: true, enterprise: true },
      { feature: __('Custom email templates', 'Eigene E-Mail-Vorlagen'),            basic: false, starter: true, team: true, enterprise: true },
      { feature: __('Own SMTP server', 'Eigener SMTP-Server'),                       basic: false, starter: false, team: true, enterprise: true },
    ],
  },
  {
    title: __('Meetings & video', 'Meetings & Video'),
    rows: [
      { feature: __('Zoom video consultations', 'Zoom-Videoberatungen'),             basic: true, starter: true, team: true, enterprise: true },
      { feature: __('Microsoft Teams', 'Microsoft Teams'),                           basic: false, starter: false, team: true, enterprise: true },
      { feature: __('GoTo Meeting / Webex', 'GoTo Meeting / Webex'),                 basic: false, starter: true, team: true, enterprise: true },
      { feature: __('Unblu video consultation', 'Unblu-Videoberatung'),              basic: false, starter: false, team: false, enterprise: true },
    ],
  },
  {
    title: __('Payments', 'Zahlungen'),
    rows: [
      { feature: __('Stripe online payments', 'Stripe Online-Zahlungen'),            basic: false, starter: true, team: true, enterprise: true },
      { feature: __('SaferPay (Klarna · TWINT · PostFinance)', 'SaferPay (Klarna · TWINT · PostFinance)'), basic: false, starter: false, team: true, enterprise: true },
    ],
  },
  {
    title: __('Integrations', 'Integrationen'),
    rows: [
      { feature: __('Bexio invoice integration', 'Bexio-Rechnungsintegration'),      basic: false, starter: false, team: true, enterprise: true },
      { feature: __('CRM/ERP (Salesforce · Dynamics · SAP)', 'CRM/ERP (Salesforce · Dynamics · SAP)'), basic: false, starter: false, team: false, enterprise: true },
      { feature: __('HubSpot CRM', 'HubSpot CRM'),                                   basic: { addon: true }, starter: { addon: true }, team: { addon: true }, enterprise: { addon: true } },
      { feature: __('Resource Planning', 'Ressourcenplanung'),                       basic: { addon: true }, starter: { addon: true }, team: { addon: true }, enterprise: { addon: true } },
    ],
  },
  {
    title: __('Branding & customization', 'Branding & Anpassung'),
    rows: [
      { feature: __('Custom logo', 'Eigenes Logo'),                                  basic: false, starter: true, team: true, enterprise: true },
      { feature: __('Corporate design (colours · domain · email)', 'Corporate Design (Farben · Domain · E-Mail)'), basic: false, starter: false, team: true, enterprise: true },
      { feature: __('Full white-label', 'Vollständiges White-Label'),                basic: false, starter: false, team: false, enterprise: true },
      { feature: __('Custom booking process', 'Individueller Buchungsprozess'),     basic: false, starter: false, team: false, enterprise: true },
    ],
  },
  {
    title: __('Workflow & automation', 'Workflow & Automatisierung'),
    rows: [
      { feature: __('Workflows engine (active count)', 'Workflow-Engine (aktive Anzahl)'), basic: '10', starter: '20', team: '50', enterprise: '100' },
      { feature: __('Condition Engine (advanced rules)', 'Condition Engine (erweiterte Regeln)'), basic: { addon: true }, starter: { addon: true }, team: { addon: true }, enterprise: { addon: true } },
    ],
  },
  {
    title: __('Analytics & reporting', 'Analytik & Reporting'),
    rows: [
      { feature: __('Basic booking stats', 'Basis-Buchungsstatistiken'),             basic: true, starter: true, team: true, enterprise: true },
      { feature: __('Reporting & data exports', 'Reporting & Datenexporte'),         basic: false, starter: false, team: true, enterprise: true },
      { feature: __('Advanced analytics dashboard', 'Erweitertes Analytics-Dashboard'), basic: { addon: true }, starter: { addon: true }, team: { addon: true }, enterprise: { addon: true } },
      { feature: __('Audit logs (30-day retention)', 'Audit-Logs (30 Tage)'),       basic: false, starter: false, team: false, enterprise: true },
    ],
  },
  {
    title: __('Security & compliance', 'Sicherheit & Compliance'),
    rows: [
      { feature: __('GDPR / nDSG compliant · Swiss-hosted', 'DSGVO- / nDSG-konform · CH-Hosting'), basic: true, starter: true, team: true, enterprise: true },
      { feature: __('Encrypted customer & appointment data', 'Verschlüsselte Kunden- & Termindaten'), basic: false, starter: false, team: true, enterprise: true },
      { feature: __('SSO (Azure AD · Entra ID · LDAP)', 'SSO (Azure AD · Entra ID · LDAP)'), basic: false, starter: false, team: false, enterprise: true },
      { feature: __('Custom roles & permissions', 'Individuelle Rollen & Berechtigungen'), basic: false, starter: false, team: false, enterprise: true },
      { feature: __('Audit-protocol compliance', 'Audit-Protokoll Compliance'),       basic: false, starter: false, team: false, enterprise: true },
      { feature: __('Dedicated infrastructure', 'Dedizierte Infrastruktur'),         basic: false, starter: false, team: false, enterprise: true },
      { feature: __('Entra ID user sync', 'Entra ID Benutzer-Synchronisation'),     basic: { addon: true }, starter: { addon: true }, team: { addon: true }, enterprise: { addon: true } },
      { feature: __('Microsoft Graph API logging', 'Microsoft Graph API Logging'),  basic: { addon: true }, starter: { addon: true }, team: { addon: true }, enterprise: { addon: true } },
    ],
  },
  {
    title: __('API & developer', 'API & Entwickler'),
    rows: [
      { feature: __('REST API access', 'REST-API-Zugang'),                            basic: false, starter: false, team: false, enterprise: true },
    ],
  },
  {
    title: __('Support', 'Support'),
    rows: [
      { feature: __('Knowledge base & self-service', 'Wissensdatenbank & Self-Service'), basic: true, starter: true, team: true, enterprise: true },
      { feature: __('Standard email support (~72h)', 'Standard E-Mail-Support (~72h)'), basic: false, starter: true, team: true, enterprise: true },
      { feature: __('Priority support + callback (~24h)', 'Priorisierter Support + Rückruf (~24h)'), basic: false, starter: false, team: true, enterprise: true },
      { feature: __('Dedicated account manager + SLA', 'Persönlicher Account-Manager + SLA'), basic: false, starter: false, team: false, enterprise: true },
    ],
  },
];
